/**
 * Card requirements → notice copy. A card declares what inputs it needs; if the
 * account doesn't have them, the card shows a clear notice + a link to go fix it
 * (instead of fake numbers). This is how we show EVERY card while nudging the
 * client to complete their setup.
 */

import type { NeedId, NeedStatus } from "@data-dashboard/shared";

export type Capability =
  | "marketingSpend" | "contributionMargin" | "channel" | "email" | "analytics"
  | "overhead" | "support" | "fulfillment" | "inventory" | "chargebacks"
  | "physicalQuantity" | "processingFees" | "customerValue" | "grossCustomerValue" | "subscriptionLifecycle"
  | "subscriptionPlanState" | "subscriptionRecurringSales" | "subscriptionOrderLinkage";

/**
 * Which card capabilities map onto a founder-fixable NEED (the shared registry).
 * A card that `requires` one of these shows the UNIFIED need warning (the
 * founder's amber "we need something → open the chat" style) instead of a
 * pretend sample number when it's unmet. Capabilities NOT listed here are
 * "connect this optional platform / coming soon" (channel, email, chargebacks,
 * …) — those keep their own connect notice; they aren't setup needs the founder
 * can just fill in. Add a row when a new capability becomes a need. */
export const CAPABILITY_NEED: Partial<Record<Capability, NeedId>> = {
  marketingSpend: "marketing_spend",
  contributionMargin: "product_costs",
  overhead: "overhead",
};

/** The PARTIAL-costing gap (founder 2026-07-11): products selling for real
 *  money with no active cost inputs. Set → every CM-derived card shows the
 *  amber "Can't calculate yet" warning (components/CmGapNotice.tsx) that opens
 *  the costs chat, instead of a number, an estimate, or a coverage %. */
export interface CmGap {
  missingProducts: number;
  /** Up to 3 product names, biggest revenue first — for the warning copy. */
  topNames: string[];
}

export interface Capabilities {
  /** Account-wide client basis. Revenue is the safe default until every
   * applicable expense is complete and the current result has been reviewed. */
  financialBasis?: "revenue" | "profit";
  marketingSpend: boolean;
  contributionMargin: boolean;
  channel: boolean;
  email: boolean;
  analytics: boolean;
  overhead: boolean;
  support: boolean;
  fulfillment: boolean;
  inventory: boolean;
  /** Any chargeback/dispute rows exist → the Operations chargeback cards compute.
   *  Optional: responses cached before the dispute feed shipped lack it (→ locked). */
  chargebacks?: boolean;
  /** Detailed BuyGoods history omits the number of physical units. Older
   * cached responses omit this flag too, so quantity views require true. */
  physicalQuantity?: boolean;
  /** Every source-backed BuyGoods fee is present for the imported history. */
  processingFees?: boolean;
  /** LTV and other customer-value results have complete source facts. */
  customerValue?: boolean;
  /** Revenue-only LTV has complete customer, order, and refund-cash history. */
  grossCustomerValue?: boolean;
  /** Subscription starts, changes, cancels, and future shipments are proven. */
  subscriptionLifecycle?: boolean;
  /** At least one named provider supplies plan status and lifecycle events. */
  subscriptionPlanState?: boolean;
  /** Connected order sources identify recurring-order sales. */
  subscriptionRecurringSales?: boolean;
  /** Plan records reliably match the imported orders used for value reporting. */
  subscriptionOrderLinkage?: boolean;
  /** Detailed or mixed BuyGoods history still has incomplete source facts. */
  buyGoodsDetailedHistoryGap?: boolean;
  /** Informational (NOT a card requirement): any order has tax dollars.
   *  False = "Include taxes" toggles have nothing to add — show them
   *  disabled-with-reason. Optional: older cached responses lack it. */
  hasTaxData?: boolean;
  /** Non-null ONLY in the PARTIAL state: some products costed, some selling
   *  uncosted (see CmGap). Optional: older cached responses lack it. */
  cmGap?: CmGap | null;
  /** The unified NEEDS list — every setup need's met/unmet state + detail,
   *  derived server-side from the same facts as the setup checklist. Drives the
   *  page banner; the cards read the capability flags above. Optional: older
   *  cached responses (and the pre-unlock locked response) lack it. */
  needs?: NeedStatus[];
}

export const REQUIREMENTS: Record<Capability, { notice: string; cta: string; to: string }> = {
  marketingSpend: {
    notice: "We can't calculate this yet — we don't know your marketing spend.",
    cta: "Add marketing spend",
    to: "/welcome",
  },
  contributionMargin: {
    notice: "We can't calculate this yet — we need your variable costs (contribution margin).",
    cta: "Set up costs",
    to: "/cm-wizard",
  },
  channel: {
    notice: "We can't break this down by channel yet — connect your ad/affiliate platform (e.g. HasOffers).",
    cta: "Connect a channel",
    to: "/connections",
  },
  email: {
    notice: "We need your email platform for this — connect one in Connections.",
    cta: "Connect email platform",
    to: "/connections",
  },
  analytics: {
    notice: "Coming soon — this needs website/funnel analytics (we don't support an analytics source yet).",
    cta: "See integrations",
    to: "/connections",
  },
  overhead: {
    notice: "Add your overhead + labor costs to see this.",
    cta: "Add overhead",
    to: "/welcome",
  },
  support: {
    notice: "Coming soon — this needs your support tool (Gorgias / Zendesk).",
    cta: "See integrations",
    to: "/connections",
  },
  fulfillment: {
    notice: "Coming soon — this needs shipping/fulfillment data.",
    cta: "See integrations",
    to: "/connections",
  },
  inventory: {
    notice: "Coming soon — this needs inventory data.",
    cta: "See integrations",
    to: "/connections",
  },
  chargebacks: {
    notice: "Coming soon — connect a dispute feed (your processor/CRM or a tool like Midigator or Chargebee) to see chargebacks.",
    cta: "See integrations",
    to: "/connections",
  },
  physicalQuantity: {
    notice: "BuyGoods did not include the number of physical units in this history, so quantity is unavailable.",
    cta: "Review BuyGoods history",
    to: "/connections",
  },
  processingFees: {
    notice: "BuyGoods did not include its source fee for every charge, so fees are unavailable.",
    cta: "Review BuyGoods history",
    to: "/connections",
  },
  customerValue: {
    notice: "Customer LTV is unavailable because this BuyGoods history is missing costs and refund details.",
    cta: "Review BuyGoods history",
    to: "/connections",
  },
  grossCustomerValue: {
    notice: "Revenue LTV is unavailable because the customer and order history is incomplete.",
    cta: "Review BuyGoods history",
    to: "/connections",
  },
  subscriptionLifecycle: {
    notice: "Subscription results are unavailable because BuyGoods did not include complete plan events.",
    cta: "Review BuyGoods history",
    to: "/connections",
  },
  subscriptionPlanState: {
    notice: "Plan health is unavailable because no connected source has provided complete plan status yet.",
    cta: "Review subscription sources",
    to: "/connections",
  },
  subscriptionRecurringSales: {
    notice: "Recurring sales are unavailable because no connected order source identifies them yet.",
    cta: "Review order sources",
    to: "/connections",
  },
  subscriptionOrderLinkage: {
    notice: "This breakdown stays hidden until subscription plans can be reliably matched to imported orders.",
    cta: "Review subscription sources",
    to: "/connections",
  },
};
// TODO: when ad/affiliate/analytics integrations land, split `channel` into
// per-provider capability flags so each notice links to the exact missing one.

/** First unmet requirement for a card, or null if it's ready. */
export function firstMissing(requires: Capability[] | undefined, caps: Capabilities | undefined): Capability | null {
  if (!requires || requires.length === 0) return null;
  // Unknown capability state must never expose a derived number during the
  // race between its metric request and the source check. A short locked state
  // is safer than flashing CAC, profit, LTV, quantity, or chargebacks that the
  // source check may reject a moment later.
  if (!caps) return requires[0] ?? null;
  return requires.find((r) => !caps[r]) ?? null;
}
